import type { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '@contexts/ThemeContext';
import { B2BLandingHeader } from './B2BLandingHeader';
import { B2BDemoLeadForm } from './B2BDemoLeadForm';
import { B2BFooter } from './B2BTestimonialsAndCTA';

/**
 * Left panel — periwinkle brand swatch (~#8b8bf5): lighter top-right → deeper bottom-left.
 */
const PERIWINKLE = {
  tr:  '#a5a5fb',
  mid: '#8b8bf5',
  bl:  '#7373e8',
} as const;

const PERIWINKLE_DARK = {
  tr: '#5a5a98',
  bl: '#3d3d6e',
} as const;

/**
 * B2B demo / contact page.
 *
 * Layout:
 *   ┌──────────────────────────────────────┐
 *   │  B2BMarketingNav (solid, no demo CTA)│
 *   ├──────────────────────────────────────┤
 *   │  Left panel 42%  │  Right panel 58%  │
 *   │  PNG image slot  │  Scrollable form  │
 *   │  Headline + list │                   │
 *   ├──────────────────────────────────────┤
 *   │  B2BFooter                           │
 *   └──────────────────────────────────────┘
 */
export function B2BDemoPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const leftPanelStyle: CSSProperties = {
    background: isDark
      ? `linear-gradient(to bottom left, ${PERIWINKLE_DARK.tr} 0%, ${PERIWINKLE_DARK.bl} 100%)`
      : `linear-gradient(to bottom left, ${PERIWINKLE.tr} 0%, ${PERIWINKLE.mid} 52%, ${PERIWINKLE.bl} 100%)`,
    borderColor: 'rgba(255,255,255,0.28)',
  };

  const illusFrameStyle: CSSProperties = {
    backgroundColor: isDark ? '#4a4a8a' : '#8b8bf5',
    borderColor:     isDark ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.40)',
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-950">

      {/* ── Same header as the landing page — always solid, no "Schedule demo" ── */}
      <B2BLandingHeader forceSolid hideDemoButton />

      {/* Spacer to push content below the fixed header (min-h-[75px]) */}
      <div className="h-[75px] flex-shrink-0" />

      {/* ── Split area ────────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0 p-4 gap-4 lg:p-6 lg:gap-6">

        {/* ════════════════════════════════════════════════════════════
         *  LEFT PANEL — brand-tinted gradient, large hero, minimal footer
         * ════════════════════════════════════════════════════════════ */}
        <motion.div
          className="hidden lg:flex lg:w-[42%] h-full min-h-0 flex-col rounded-2xl shadow-xl overflow-hidden border p-8 lg:p-10"
          style={leftPanelStyle}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-2xl xl:text-[1.75rem] font-bold leading-snug tracking-tight text-white">
            Book a demo with our team — we&apos;ll walk you through GoGrabJob Business, end to end.
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed max-w-md text-white/90">
            Real product specialists. Ask questions and see how your team would run hiring on GoGrabJob.
          </p>

          <div className="mt-6 w-full flex-1 min-h-0 flex flex-col">
            <div
              className="w-full h-[min(38vh,340px)] min-h-[180px] rounded-3xl overflow-hidden border shadow-sm"
              style={illusFrameStyle}
            >
              <img
                src="/images/b2b-demo-hero.png"
                alt="GoGrabJob Business — book a demo"
                className="block h-full w-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          <p className="mt-auto pt-6 text-center text-xs text-white/70">
            Free · No credit card · No obligation
          </p>
        </motion.div>

        {/* ════════════════════════════════════════════════════════════
         *  RIGHT PANEL — scrollable form
         * ════════════════════════════════════════════════════════════ */}
        <div
          className="w-full lg:w-[58%] flex flex-col overflow-y-auto rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800/30"
          style={{
            background: isDark
              ? '#141824'
              : 'linear-gradient(135deg, #ffffff 0%, #faf9ff 55%, #f2f0fd 100%)',
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55 }}
            className="w-full max-w-lg mx-auto px-5 pt-4 pb-4 lg:px-6 lg:pt-5 lg:pb-5"
          >
            {/* Heading — compact so the form starts high */}
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-violet-500 dark:text-violet-400 mb-1">
                Book a demo
              </p>
              <h2 className="text-[1.4rem] font-bold text-gray-900 dark:text-white mb-1">
                See GoGrabJob Business live
              </h2>
              <p className="text-[0.875rem] text-gray-500 dark:text-[#94a3b8] leading-snug">
                Tell us about your team — we'll reach out within{' '}
                <span className="font-semibold text-gray-700 dark:text-gray-300">4 business hours</span>.
              </p>
            </div>

            <B2BDemoLeadForm />

            {/* Privacy / terms */}
            <div className="text-center mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
              <p className="text-xs text-gray-400">
                <button
                  type="button"
                  onClick={() => window.open('/privacy-policy', '_blank', 'noopener,noreferrer')}
                  className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  Privacy Policy
                </button>
                {' · '}
                <button
                  type="button"
                  onClick={() => window.open('/terms-of-service', '_blank', 'noopener,noreferrer')}
                  className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  Terms of Service
                </button>
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────────────── */}
      <B2BFooter />
    </div>
  );
}

